import { Fragment } from 'react';

import { confirmColor, confirmBorderColor, cancelColor } from '../constants/Styles';
import { HorizontalButtonPairView } from '../components/DRViewUtils';
import { Destination } from '../constants/containerManagerInterfaces';
import CornerGripperLogo from '../components/CornerGripperLogo';
import PinCode from '../components/PinCode';
import { textIncludesCode } from '../utils/commonUtils';

import './PinCodeScreen.scss';
import { getConfig } from '../utils/configUtils';

export interface PinCodeScreenProps {
  clickCancel: () => void;
  clickConfirm: () => void;
  tapToReload: () => void;
  destinations: Destination[];
  givenTo: GivenTo;
  setGivenTo: (data: GivenTo) => void;
  codeRefreshed: boolean;
}

interface GivenToData {
  value: string;
  error: string;
}

interface GivenTo {
  [any: string]: GivenToData;
}

export default function PinCodeScreen({
  clickCancel,
  clickConfirm,
  tapToReload,
  destinations,
  givenTo,
  setGivenTo,
  codeRefreshed,
}: PinCodeScreenProps) {
  const validateForm = () => Object.keys(givenTo).every((g) => givenTo[g].value.trim().length && !givenTo[g].error);

  const buttonSettings = [
    {
      phrase: 'GO BACK',
      fillColor: 'transparent',
      borderColor: cancelColor,
      fontColor: cancelColor,
      action: clickCancel,
    },
    {
      phrase: 'CONTINUE',
      fillColor: confirmColor,
      borderColor: confirmBorderColor,
      fontColor: 'white',
      disabled: !validateForm(),
      action: clickConfirm,
    },
  ];

  const handleInputChange = (value: string, destinationId: number) => {
    const givenToState = { ...givenTo };
    givenToState[destinationId].value = value;

    const destination = destinations.find((d) => d.id === destinationId);

    if (destination && textIncludesCode(destination.pinCode, givenToState[destinationId].value)) {
      givenToState[destinationId].error = 'Do not include the code in this note.';
    } else {
      givenToState[destinationId].error = '';
    }

    setGivenTo(givenToState);
  };

  const getTitle = () => {
    if (destinations.length > 0) return `Send multi-factor delivery code${destinations.length > 1 ? 's' : ''}`;
    return `All of Moxi's drawers have been loaded.`;
  };

  const getSubtitle = () => {
    const supportNumber = getConfig().site?.supportNumber;
    if (destinations.length === 0)
      return (
        <div className="content__subtitle--narrow">{`You will need to request a new delivery. Contact Moxi support ${
          supportNumber ? `(${supportNumber}) ` : ''
        } to address this issue.`}</div>
      );
    if (destinations.length > 1) {
      return "Send the following codes to each location's recipient and note who received the code";
    }
    return 'Send the following code to the recipient and note who received the code.';
  };

  return (
    <div className="pin-code-screen">
      <CornerGripperLogo tapToReload={tapToReload} />
      <div className={`pin-code-screen__content${destinations.length > 2 ? ' pin-code-screen__content--tall' : ''}`}>
        {destinations.length === 0 && (
          <img
            className="content__error-img"
            alt="Moxi help"
            draggable="false"
            src={require('../assets/images/moxi-question.png')}
          />
        )}
        <h1 className="content__title-text">{getTitle()}</h1>
        <h3 className="content__subtitle-text">
          {getSubtitle()}
          {!!codeRefreshed && destinations.length > 0 && (
            <div className="content__subtitle-text--shake">
              {destinations.length > 1
                ? 'The codes have refreshed to ensure delivery security, please send these new codes.'
                : 'The code has refreshed to ensure delivery security, please send this new code.'}
            </div>
          )}
        </h3>
        {destinations.length ? (
          <div className="content__details">
            {destinations.map((destination, index) => (
              <Fragment key={destination.id}>
                <PinCode
                  drawer={destination.drawer}
                  id={destination.id}
                  inline={destinations.length > 1}
                  inputValue={givenTo[destination.id]?.value}
                  inputError={givenTo[destination.id]?.error}
                  locationName={destination.name}
                  note={destination.note}
                  onInputChange={(value) => handleInputChange(value, destination.id)}
                  pinCode={destination.pinCode}
                ></PinCode>
                {destinations.length > 1 && index < destinations.length - 1 && <div className="divider" />}
              </Fragment>
            ))}
          </div>
        ) : null}
      </div>
      <div className="pin-code-screen__footer">
        <HorizontalButtonPairView buttonSettings={buttonSettings} />
      </div>
    </div>
  );
}
